import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Calculator, Users, Rocket, AlertCircle, DollarSign, CheckCircle2, Cog, Calendar } from 'lucide-react';

const GettingStartedSlide = () => {
  const steps = [
    {
      icon: FileText,
      title: "Week 1: Intake & Analysis",
      description: "We collect your fee schedules, credentials, and practice rules. We analyze your current AR and denial rates to set a baseline."
    },
    {
      icon: Cog,
      title: "Week 2-3: Setup & Calibration",
      description: "Our team builds your custom rules engine and configures the AI agents. Your staff does zero work during this phase.",
      note: "Fully hands-off except for check-in meetings."
    },
    {
      icon: Rocket,
      title: "Week 4: Go Live",
      description: "Your Back Office AI workforce activates. Claims are scrubbed, benefits verified, and payments posted automatically."
    }
  ];

  return (
    <div className="h-full w-full bg-[#0B1121] text-white flex flex-col items-center justify-center p-12 font-sans overflow-hidden relative">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-900/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-900/20 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />

        <div className="flex flex-row gap-12 max-w-7xl w-full z-10 h-full items-center">
            
            {/* Left Column: Title & Requirements */}
            <div className="w-1/3 flex flex-col justify-center gap-8">
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
                        <Rocket className="w-4 h-4" />
                        <span>Back Office Launch</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight">
                        Getting Started with <span className="text-blue-400">Back Office</span>
                    </h2>
                    <p className="text-slate-400 text-lg leading-relaxed">
                        A structured 4-week ramp-up to modernize your billing department. We do the heavy lifting so you don't have to.
                    </p>
                </motion.div>

                {/* Requirements Card */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
                >
                    <div className="flex items-center gap-3 mb-4 text-amber-400">
                        <AlertCircle className="w-6 h-6" />
                        <h3 className="text-lg font-bold">Qualification Requirement</h3>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center shrink-0 border border-amber-500/20">
                            <DollarSign className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-white mb-1">$50k+</div>
                            <p className="text-sm text-slate-400">
                                Monthly collections minimum required to partner with us.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Right Column: Steps Timeline */}
            <div className="w-2/3">
                <div className="relative pl-8 md:pl-12 py-4">
                    {/* Vertical Line */}
                    <div className="absolute left-[27px] md:left-[31px] top-4 bottom-12 w-0.5 bg-gradient-to-b from-blue-600/50 to-indigo-600/50" />

                    <div className="space-y-12">
                        {steps.map((item, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="flex gap-6 md:gap-8 relative group"
                            >
                                {/* Icon Dot */}
                                <div className="relative z-10 flex-shrink-0">
                                   <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center shadow-xl group-hover:border-blue-500 transition-colors duration-300">
                                        <item.icon className="w-6 h-6 md:w-7 md:h-7 text-blue-400" />
                                   </div>
                                </div>
                                
                                {/* Content */}
                                <div className="pt-2 flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-xs font-bold text-blue-500 uppercase tracking-wider">Step {index + 1}</span>
                                        <h3 className="text-xl font-bold text-white">{item.title}</h3>
                                    </div>
                                    <p className="text-slate-400 leading-relaxed mb-2 text-lg">
                                        {item.description}
                                    </p>
                                    {item.note && (
                                        <div className="inline-flex items-center gap-2 text-xs font-bold text-green-400 bg-green-900/20 px-3 py-1.5 rounded-lg border border-green-900/30 mt-1">
                                            <CheckCircle2 className="w-3 h-3 shrink-0" />
                                            {item.note}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    </div>
  );
};

export default GettingStartedSlide;
